<script setup>
import { ref, onMounted } from 'vue';
import Button from 'primevue/button';
import { Link } from '@inertiajs/vue3';
import { useSteperStore } from '@/Pages/Scheduling/Stores/steper.js';
import Skeleton from 'primevue/skeleton';
import { usePage, router } from '@inertiajs/vue3';
import { formatInternationalNumber } from '@/Services/phone';

const props = defineProps({
   id: Number
});

const page = usePage();
const clinicUuid = page.props.clinic.uuid;

const professional = ref(null);
const logoUrl = page.props.clinicLogo;
const countryCode = import.meta.env.VITE_APP_COUNTRY_CODE || 'BR';
const steperStore = useSteperStore();

const isClinicPage = window.location.pathname.endsWith('/clinic');

const loadProfessionalInfo = async () => {
   try {
      const response = await axios.get(route('scheduling.get.professional.info', { code: page.props.schedulingCode, id: props.id }));
      professional.value = response.data;
   } catch (error) {
      console.error(error);
   }
};

const goToNewSchedule = (professionalCode) => {

   // check if it is already on this route, and if so just scroll the page to the bottom
   if (steperStore.step === 1 && window.location.pathname === new URL(route('scheduling.professional.new', professionalCode)).pathname) {
      window.scrollTo(0, document.body.scrollHeight);
   } else if (steperStore.step > 1) {
      window.location.href = route('scheduling.professional.new', professionalCode);
   } else {
      window.location.href = route('scheduling.professional.new', professionalCode);
   }
};

const formatClinicAddress = (loc) => {
  if (!loc) return '';

  const street = String(loc.street_address ?? '').trim();
  const number = String(loc.number ?? '').trim();
  const complement = String(loc.complement ?? '').trim();
  const neighborhood = String(loc.neighborhood ?? '').trim();
  const city = String(loc.city ?? '').trim();

  let line = [street, number].filter(Boolean).join(', ');
  if (complement) line += ` - ${complement}`;
  if (neighborhood) line += `, ${neighborhood}`;
  if (city) line += ` (${city})`;

  return line;
};


onMounted(() => {
   loadProfessionalInfo();
});
</script>


<template>
   <div id="professionalInfoCard" class="w-full flex items-center justify-center lg:h-screen" v-if="professional">
      <div class="bg-white rounded-2xl p-8 lg:p-10 w-fit flex flex-col my-5 mx-5 lg:mx-6 lg:my-0">
         <div class="gap-2 flex flex-col items-start justify-start">
            <div v-if="logoUrl" class="flex justify-start items-center">
               <img v-if="logoUrl && logoUrl.length > 3" :src="logoUrl" alt="Logo" class="w-44 h-24" />
            </div>
            <h1 class="text-black font-semibold text-2xl">{{ $t('self_schedling') }}</h1>
            <p class="text-[#3F4254] font-medium">{{ $t('self_scheduling.new_appointment_prompt') }}</p>
            <h2 class="text-[var(--agendart-primary)] font-medium">{{ $t('self_scheduling.professional_details') }}:
            </h2>
         </div>
         <div class="flex items-center gap-4 my-2">
            <img :src="professional.profile_photo_path_url" alt="" class="rounded-xl w-16"
               v-if="professional.profile_photo_path">
            <div class="bg-[var(--agendart-light)] h-16 w-16 flex items-center justify-center rounded-md" v-else>
               <span class="uppercase text-[var(--agendart-dark)] font-medium text-lg">
                  {{ professional.name.charAt(0) }}
               </span>
            </div>
            <h1 class="text-[#181C32] font-semibold text-lg">{{ professional.name }}</h1>
         </div>

         <p class="my-1"><span class="font-medium">{{ $t('self_scheduling.attendance_time') }}: </span>{{
            professional.professional_clinic_setting?.session_duration }} {{ $t('self_scheduling.minutes') }}</p>
         <div v-if="professional.professional_clinic_location">
            <p class="my-1"><span class="font-medium">Endereço: </span>{{ formatClinicAddress(professional.professional_clinic_location) }}</p>
            <p class="my-1"><span class="font-medium">CEP: </span>{{ professional.professional_clinic_location?.zip_code
               }}</p>
         </div>

         <p class="my-1"><span class="font-medium">Email: </span>{{ professional.email }}</p>

         <div v-if="professional.professional_clinic_data">
            <p v-if="professional.professional_clinic_data.cell_phone" class="my-1"><span class="font-medium">{{ $t('phone') }}: </span>{{
               formatInternationalNumber(professional.professional_clinic_data.cell_phone, countryCode) }}</p>

            <p v-if="professional.professional_clinic_data?.observation">
               <span class="font-medium my-1">{{ $t('observation') }}</span>
               <br>
               <span v-html="professional.professional_clinic_data?.observation"></span>
            </p>
         </div>

         <div v-if="!isClinicPage" class="mt-6 w-full flex flex-col lg:flex-row justify-between gap-2 lg:gap-4">
            <Button severity="primary" class="w-full"
               @click="goToNewSchedule(professional.professional_clinic_setting?.code)">
               {{ $t('new_schedule_btn') }}
            </Button>
            <Link class="w-full" :href="route('scheduling.to.alter', professional.professional_clinic_setting?.code)">
            <Button severity="primary" light class="w-full">{{ $t('self_scheduling.change_appointment') }}</Button>
            </Link>
         </div>
         <div v-else class="mt-6 w-full flex flex-col lg:flex-row justify-between gap-2 lg:gap-4">
            <Link class="w-full" :href="route('scheduling.to.alter', professional.professional_clinic_setting?.code)">
            <Button severity="primary" light class="w-full">{{ $t('self_scheduling.change_appointment') }}</Button>
            </Link>
         </div>
      </div>
   </div>

   <div class="w-full flex items-center justify-center lg:h-screen" v-else>
      <div class="bg-white rounded-2xl p-8 lg:p-12 w-fit flex flex-col mx-5">
         <div class="gap-2 flex flex-col items-start justify-start">
            <div v-if="logoUrl" class="flex justify-start items-center">
               <img v-if="logoUrl && logoUrl.length > 3" :src="logoUrl" alt="Logo" class="w-44 h-24" />
            </div>
            <h1 class="text-black font-semibold text-2xl">{{ $t('self_schedling') }}</h1>
            <p class="text-[#3F4254] font-medium"> {{ $t('self_scheduling.new_appointment_prompt') }}</p>
            <h2 class="text-[var(--agendart-primary)] font-medium">{{ $t('self_scheduling.professional_details') }}:
            </h2>
            <Skeleton width="10rem" height="4rem"></Skeleton>
            <Skeleton width="10rem" class="mb-2"></Skeleton>
            <Skeleton width="5rem" class="mb-2"></Skeleton>
            <Skeleton width="5rem" class="mb-2"></Skeleton>
            <Skeleton class="mb-2"></Skeleton>
            <Skeleton height="2rem" class="mb-2"></Skeleton>
         </div>
      </div>
   </div>
</template>

<style>
div#professionalInfoCard a {
   text-decoration: underline;
   color: var(--agendart-primary) !important;
   cursor: pointer;
}
</style>
